/**
 * Botão e link com a mesma aparência.
 *
 * As variantes saem direto do protótipo:
 *  - `primaria`   nav "Fale Conosco" e hero "Ver Bikes Novas" (brand-700, pill)
 *
 * O protótipo usava brand-600 aqui, mas branco sobre #059669 dá 3,77:1 e
 * reprova o AA do WCAG (4,5:1 para texto normal). brand-700 dá 5,48:1 e é
 * visualmente quase idêntico — o axe apontou isto em 5 elementos da home.
 *  - `branca`     hero "Ver Bikes Usadas" e o CTA da faixa verde
 *  - `whatsapp`   botão dos cards novos (#25D366, rounded-xl)
 *  - `escura`     botão dos cards usados (gray-900, rounded-lg, menor)
 */
import type { ButtonHTMLAttributes, ReactNode } from 'react'

import { cn } from '../lib/cn.js'
import { Icone, type NomeIcone } from './Icone.jsx'

export type VarianteBotao = 'primaria' | 'branca' | 'whatsapp' | 'escura' | 'fantasma'
export type TamanhoBotao = 'sm' | 'md' | 'lg'

const VARIANTES: Record<VarianteBotao, string> = {
  primaria: 'bg-brand-700 hover:bg-brand-800 text-white shadow-md hover:shadow-lg',
  branca: 'bg-white hover:bg-gray-100 text-gray-900 shadow-lg hover:shadow-xl',
  whatsapp: 'bg-whatsapp hover:bg-whatsapp-escuro text-white',
  escura: 'bg-gray-900 hover:bg-gray-800 text-white',
  fantasma: 'bg-transparent text-gray-600 hover:bg-gray-100 hover:text-brand-600',
}

const TAMANHOS: Record<TamanhoBotao, string> = {
  // min-h-11 = 44px, o mínimo recomendado para alvo de toque.
  sm: 'min-h-11 px-4 py-2 text-sm',
  md: 'min-h-11 px-5 py-2.5 text-base',
  lg: 'min-h-12 px-8 py-3.5 text-base',
}

const BASE =
  'inline-flex items-center justify-center gap-2 font-semibold transition ' +
  'disabled:cursor-not-allowed disabled:opacity-60'

interface PropsComuns {
  variante?: VarianteBotao
  tamanho?: TamanhoBotao
  /** `pill` = rounded-full (nav/hero); `caixa` = rounded-xl (cards). */
  forma?: 'pill' | 'caixa' | 'caixa-sm'
  icone?: NomeIcone
  blocoCompleto?: boolean
  children: ReactNode
  className?: string
}

const FORMAS = {
  pill: 'rounded-full',
  caixa: 'rounded-xl',
  'caixa-sm': 'rounded-lg',
} as const

function montarClasses(p: PropsComuns): string {
  return cn(
    BASE,
    VARIANTES[p.variante ?? 'primaria'],
    TAMANHOS[p.tamanho ?? 'md'],
    FORMAS[p.forma ?? 'pill'],
    p.blocoCompleto && 'w-full',
    p.className,
  )
}

type PropsBotao = PropsComuns &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'children'>

export function Botao({
  variante,
  tamanho,
  forma,
  icone,
  blocoCompleto,
  children,
  className,
  type = 'button',
  ...resto
}: PropsBotao) {
  return (
    <button
      type={type}
      className={montarClasses({ variante, tamanho, forma, blocoCompleto, children, className })}
      {...resto}
    >
      {icone && <Icone nome={icone} className="text-lg" />}
      {children}
    </button>
  )
}

interface PropsLinkBotao extends PropsComuns {
  href: string
  /** Links externos (WhatsApp, Instagram) abrem em nova aba com rel seguro. */
  externo?: boolean
  'aria-label'?: string
  /** Efeito colateral no clique — ex.: fechar o menu mobile. */
  onClick?: () => void
}

/**
 * Mesma aparência, mas é um `<a>` de verdade.
 *
 * Importante para os botões de WhatsApp: o protótipo usava
 * `<button onclick="window.open(...)">`, o que quebra clique do meio,
 * "abrir em nova aba", long-press no celular e é anunciado como botão (não
 * link) por leitores de tela. Bloqueador de popup também interfere.
 */
export function LinkBotao({
  href,
  externo,
  variante,
  tamanho,
  forma,
  icone,
  blocoCompleto,
  children,
  className,
  ...resto
}: PropsLinkBotao) {
  return (
    <a
      href={href}
      {...(externo ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      className={montarClasses({ variante, tamanho, forma, blocoCompleto, children, className })}
      {...resto}
    >
      {icone && <Icone nome={icone} className="text-lg" />}
      {children}
    </a>
  )
}
